import React, { useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { AlertTriangle, Info } from 'lucide-react';

import { account } from '@redux/account';
import { Credits } from 'components/Icon/Credits';
import { useDismissOnOutside } from 'hooks/useDismissOnOutside';
import { formatCredits } from 'utils/formatCredits';

const DetailRow = ({ label, children }) => (
  <div className="flex items-center justify-between gap-2">
    <span className="text-ui-3">{label}</span>
    <span className="flex flex-none items-center whitespace-nowrap font-medium text-ui-accent">{children}</span>
  </div>
);

export const CreditsDisplay = ({ isCollapsed }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [showDetails, setShowDetails] = useState(false);
  const containerRef = useRef(null);

  const credits = useSelector(account.selectors.credits);
  const user = useSelector(account.selectors.user);
  const dailyCost = useSelector(account.selectors.dailyCost);
  const subscriptionDays = useSelector(account.selectors.subscriptionDays);

  const hasUserData = user && Object.keys(user).length > 0;
  const isSubscriptionExpired = hasUserData && !credits;
  const extraDevicesCount = user?.device_subscriptions?.extra_devices_count || 0;
  const extraCost = dailyCost - 1.0;
  const isLowCredits = subscriptionDays <= 7 && subscriptionDays > 0;

  // The panel is nested inside containerRef (not portalled), so one ref covers both.
  // Also gains Escape dismissal, which the hand-rolled version never had.
  useDismissOnOutside(showDetails, () => setShowDetails(false), [containerRef]);

  if (isCollapsed) {
    return (
      <div className="px-2 py-3">
        <div
          className={classNames(
            'flex flex-col items-center justify-center p-2 rounded-field',
            'transition-all duration-200 ease-out cursor-default',
            {
              'bg-ui-danger-wash text-ui-danger': isSubscriptionExpired,
              'border border-ui-accent-edge bg-ui-accent-wash text-ui-accent': isLowCredits && !isSubscriptionExpired,
              'border border-ui-hair-3 bg-ui-overlay text-ui-2': !isSubscriptionExpired && !isLowCredits
            }
          )}
          title={isSubscriptionExpired ? t('navBar.subscriptionExpired') : `${formatCredits(credits)} ${t('navBar.credits')}`}
        >
          {isSubscriptionExpired ? (
            <AlertTriangle className="mb-1" size={18} />
          ) : (
            <>
              <Credits className="text-ui-accent mb-1" size={18} />
              <span className="text-xs font-medium">
                {formatCredits(credits)}
              </span>
            </>
          )}
        </div>
      </div>
    );
  }

  // Expired state - simplified, action-focused design
  if (isSubscriptionExpired) {
    return (
      <div className="p-3">
        <Link
          to="/app/plans"
          className={classNames(
            'w-full flex items-center justify-center gap-2 p-3 rounded-field',
            'bg-ui-danger-wash border border-ui-danger-edge hover:border-ui-danger',
            'transition-all duration-200 ease-out'
          )}
        >
          <AlertTriangle className="text-ui-danger shrink-0" size={16} />
          <span className="text-xs font-medium text-ui-danger">
            {t('navBar.subscriptionExpired')}
          </span>
        </Link>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative p-3">
      {/* Main display */}
      <button
        onClick={() => setShowDetails(!showDetails)}
        className={classNames(
          'w-full flex items-center justify-between p-3 rounded-field',
          'transition-all duration-200 ease-out cursor-pointer',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-ui-accent-ring',
          {
            'border border-ui-accent-edge hover:border-ui-accent bg-ui-accent-wash hover:bg-ui-accent-wash': isLowCredits,
            'border border-ui-hair-3 hover:border-ui-hair-3 bg-ui-overlay hover:bg-ui-elevated': !isLowCredits
          }
        )}
      >
        <div className="flex items-center">
          <Credits className="text-ui-accent mr-2.5 shrink-0" size={18} />
          <div className="flex flex-col items-start">
            <span className="text-sm font-medium text-ui-2">
              {formatCredits(credits)} {t('navBar.credits')}
            </span>
            <span className="text-xs text-ui-4">
              = {subscriptionDays} {t('navBar.days')}
            </span>
          </div>
        </div>
        <Info
          size={14}
          className={classNames(
            'transition-all duration-200',
            {
              'text-ui-4': !showDetails,
              'text-ui-3': showDetails
            }
          )}
        />
      </button>

      {/* Details popover */}
      <div
        className={classNames(
          'absolute bottom-full left-2 right-2 mb-2 z-50',
          'bg-ui-elevated border border-ui-hair-3 rounded-field shadow-menu p-3',
          'transition-all duration-200 ease-out origin-bottom',
          {
            'opacity-100 scale-100 pointer-events-auto': showDetails,
            'opacity-0 scale-95 pointer-events-none': !showDetails
          }
        )}
      >
        <h4 className="text-ui text-sm font-medium mb-3">
          {t('navBar.subscriptionDetails')}
        </h4>

        <div className="space-y-2 text-sm">
          <DetailRow label={t('navBar.daysRemaining')}>
            {formatCredits(subscriptionDays)} {t('navBar.days')}
          </DetailRow>

          <DetailRow label={t('navBar.totalCredits')}>
            <Credits className="mr-1.5" size={14} />
            {formatCredits(credits)}
          </DetailRow>

          {dailyCost > 1.0 && (
            <DetailRow label={t('navBar.dailyCost')}>
              <Credits className="mr-1.5" size={14} />
              {formatCredits(dailyCost)} {t('navBar.perDay')}
            </DetailRow>
          )}
        </div>

        <p className="text-xs text-ui-4 mt-3 pt-3 border-t border-ui-hair-3">
          {dailyCost > 1.0
            ? t('navBar.creditsExplanationWithExtra', {
                extraCost: formatCredits(extraCost),
                count: extraDevicesCount
              })
            : t('navBar.creditsExplanation')
          }
        </p>

        {isLowCredits && location.pathname !== '/app/plans' && (
          <Link
            to="/app/plans"
            onClick={() => setShowDetails(false)}
            className="mt-3 block w-full text-center py-2 px-3 rounded-tile text-sm font-medium bg-ui-accent hover:bg-ui-accent-hover text-black transition-all duration-200 ease-out"
          >
            {t('navBar.goToPlans')}
          </Link>
        )}
      </div>
    </div>
  );
};
